/**
 * The shared Map lens — ONE renderer, two consumers (#2276).
 *
 * The record page's relationship workspace and the canonical Address form draw
 * the same component with the same resolved data. That is the whole point: the
 * platform has had a map before, as a bespoke CRM panel with a hard-coded
 * relationship key and its own query, and #2267 deleted it. What comes back is a
 * control-kit component that cannot do any of that.
 *
 * ── IT IS DATA-FREE, AND THAT IS ENFORCED BY WHAT IT CANNOT IMPORT ─────────
 * `packages/ui` may not import `@solum/sdk`, know an entity key, or fetch
 * anything (`ui-and-experience.md` §1). So this component:
 *
 *   · receives PINS that a host already resolved under the caller's own RLS;
 *   · receives the TILE SOURCE as a prop — no provider is named here, no URL is
 *     defaulted, no API key exists to leak. A host with no tile source gets an
 *     explicit refused state, never a blank rectangle;
 *   · never geocodes, never routes, never decides who may open what. `onOpen`
 *     is the host's canonical record destination — the same one Grid and Cards
 *     use — and a pin the host marked unopenable is not a button at all;
 *   · never writes. The Address form's draft preview is the HOST passing a pin
 *     built from current form state, which is why "the map does not autosave"
 *     is true here by construction rather than by discipline.
 *
 * ── THE PICTURE IS DECORATION OVER A STRUCTURE THAT ALREADY WORKS ──────────
 * The metro lens established the rule (`Connections.tsx`): the accessible LIST
 * is the real thing and the drawing sits over it. So every pin is a real
 * `<button>` in the document, there is a synchronized result list beside the
 * canvas, and a screen reader never meets a canvas at all. A node-link picture
 * would have been prettier and unreachable.
 *
 * ── NOTHING DISAPPEARS ─────────────────────────────────────────────────────
 * Records the recipe cannot place do not vanish: they are listed under
 * `Location unavailable`, named, and openable. A map that silently drops the
 * rows it cannot draw is worse than no map, because the reader cannot tell the
 * difference between "there are none" and "there are none I can show you".
 */
import {
  useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode,
} from 'react'
import { cx } from '../controls/cx'
import {
  MAX_ZOOM, MIN_ZOOM, clampLat, clampZoom, clusterPoints, fitView, mercX, mercY,
  project, tileUrl, tilesFor, wrapLng,
  type Cluster, type MapView,
} from './projection'
import s from './MapLens.module.css'

/**
 * Where raster tiles come from. Supplied by the HOST, which reads it from
 * governed settings — a provider or a key appearing in this file would be the
 * defect `crm/geo.ts` documents at length (#524: an embed carries its key in the
 * page, so there is no version of a keyed provider that is also governed).
 */
export interface MapTileSource {
  /** A `{z}/{x}/{y}` template. */
  urlTemplate: string
  /** Required: a raster source that does not credit its data is a licence breach. */
  attribution: string
  maxZoom?: number
}

/** One record the recipe placed. Everything here is already resolved. */
export interface MapPin {
  /** Stable identity on this surface. */
  key: string
  entity: string
  id: string
  /** The canonical record title — the host resolved it, this never invents one. */
  title: string
  /** Governed secondary text, when the recipe or the entity's title names one. */
  subtitle?: string
  /**
   * The governed address/relationship TYPE label — "Registered seat", "Billing".
   * Resolved from metadata by the host. A raw code must never reach here: the
   * preview card renders this verbatim, so a `billing` in the data is a `billing`
   * on the screen.
   */
  typeLabel?: string
  lat: number
  lng: number
  /** Which relationship group this record came from, when there is more than one. */
  groupLabel?: string
  /** `false` when the host has no destination for it — then it is not a button. */
  openable?: boolean
}

/** One record the recipe could NOT place. Named, never dropped. */
export interface MapUnplaceableRecord {
  key: string
  entity: string
  id: string
  title: string
  typeLabel?: string
  groupLabel?: string
  /** Why, in the host's words. Absent is fine — the heading already says it. */
  reason?: string
  openable?: boolean
}

export interface MapLensProps {
  pins: readonly MapPin[]
  /** Records with missing or invalid coordinates. They are LISTED. */
  unplaceable?: readonly MapUnplaceableRecord[]
  /**
   * The tile seam. `null`/absent renders an explicit "no map provider" state
   * with the pins still listed — offline and refused are visible, not blank.
   */
  tiles?: MapTileSource | null
  /** The canonical record destination. Absent ⇒ nothing here is a button. */
  onOpen?: (record: MapPin | MapUnplaceableRecord) => void
  /**
   * `workspace` draws the synchronized result list beside the canvas.
   * `record` is the Address form's single focused pin, where a list of one is
   * noise — the pin still carries the whole accessible name.
   */
  presentation?: 'workspace' | 'record'
  heading?: ReactNode
  /**
   * The heading LEVEL this lens's title takes, and therefore the level its two
   * lists take beneath it.
   *
   * A prop rather than a constant, and it is not a style choice: heading order
   * is an accessibility contract, the two consumers sit at different depths, and
   * a component that guessed would be wrong on one of them. `ci / a11y` catches
   * it — this component's own axe case did, twice: first as bare h4 lists with
   * no parent heading, then as a section whose title was a `<span>`, which left
   * the lists parented by nothing at all.
   *
   * The same reason `RecordShell` takes `presentation` (`ui-and-experience.md`
   * §8): a pane sits under the page's own h1, so its title must be an h2.
   *
   * 3 by default — the relationship workspace, where the lens is a peer of the
   * relationship sections beside it, under the workspace's own h2.
   */
  headingLevel?: 2 | 3 | 4
  /** A refusal or a failure, already worded by the host. */
  problem?: ReactNode
  busy?: boolean
  empty?: ReactNode
  /** Canvas height in pixels. The width is whatever the container gives. */
  height?: number
  testId?: string
}

const CLUSTER_CELL_PX = 56

/**
 * The width to project against before the canvas has measured itself.
 *
 * MEASURED, and it is the defect this constant exists to close: gating the
 * marks on `width > 0` meant a canvas that reported 0 on its first layout drew
 * its tiles, its controls and its attribution and NOT ONE PIN — a blank map
 * beside a populated list, which is indistinguishable from a broken one. Six
 * browser cases failed on it while the list beside them was perfect.
 *
 * So the projection falls back instead of refusing. A pin a few pixels from
 * where it will settle once the ResizeObserver fires is a self-correcting
 * cosmetic error; a canvas with nothing on it is the blank surface this
 * platform keeps refusing to ship.
 */
const ASSUMED_WIDTH = 640

export function MapLens({
  pins, unplaceable = [], tiles, onOpen, presentation = 'workspace',
  heading = 'Map', headingLevel = 3, problem, busy, empty = 'Nothing here has a location yet.',
  height = 360, testId = 'map-lens',
}: MapLensProps) {
  const canvas = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ width: 0, height })
  const [view, setView] = useState<MapView | null>(null)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const pinNodes = useRef(new Map<string, HTMLButtonElement>())
  const domId = useId()

  /* ── the viewport ──────────────────────────────────────────────────────── */
  useEffect(() => {
    const measure = () => setSize({ width: canvas.current?.clientWidth ?? 0, height })
    measure()
    // ResizeObserver, not a window listener: this component lives in a split
    // pane and in a drawer, and both change width without the window doing
    // anything at all. jsdom has no ResizeObserver, hence the guard.
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }
    const ro = new ResizeObserver(measure)
    if (canvas.current) ro.observe(canvas.current)
    return () => ro.disconnect()
  }, [height])

  /**
   * The fit, recomputed only from what it depends on. This is the value a reset
   * restores, so it must not close over the current view — a reset that drifted
   * would be a control that does something slightly different every press.
   */
  const fit = useMemo(
    () => fitView(pins, size.width || ASSUMED_WIDTH, size.height),
    // The pins' POSITIONS are what the fit depends on; re-fitting because a
    // title changed would move the map under somebody mid-read.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pins.map((p) => `${p.lat},${p.lng}`).join('|'), size.width, size.height])

  /* An explicit view wins; otherwise the fit does. Adopting the fit into state
     would make the map jump the first time the container resized. */
  const active = view ?? fit
  const reset = useCallback(() => { setView(null); setOpenKey(null) }, [])

  useEffect(() => {
    // A pin that is no longer on the surface cannot keep a card open over the
    // place it used to be. "Data changes" is the half of the one-card rule that
    // pan/zoom does not cover.
    if (openKey && !pins.some((p) => p.key === openKey)) setOpenKey(null)
  }, [pins, openKey])

  const clusters = useMemo(
    () => clusterPoints(pins, active, size.width || ASSUMED_WIDTH, size.height, CLUSTER_CELL_PX),
    [pins, active, size.width, size.height])

  /* ── panning ───────────────────────────────────────────────────────────── */
  const drag = useRef<{ id: number; x: number; y: number; view: MapView } | null>(null)
  const onCanvasPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return          // a pin's own press is not a pan
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, view: active }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onCanvasPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    const scale = 256 * 2 ** d.view.zoom
    const dx = (e.clientX - d.x) / scale
    const dy = (e.clientY - d.y) / scale
    setView(panBy(d.view, dx, dy))
  }
  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (drag.current?.id === e.pointerId) drag.current = null
  }

  const zoomBy = (delta: number) =>
    setView({ ...active, zoom: clampZoom(active.zoom + delta) })

  /* ── the preview card's input rules (owner addendum) ───────────────────── */
  /**
   * A touch's FIRST tap reveals and does not navigate.
   *
   * The rule is one sentence and it produces the right behaviour for all three
   * input types on its own: **a pin whose card was already open when the press
   * began opens the record; one whose card was closed opens the card.**
   *
   *   pointer   hovering already opened it, so the click opens the record;
   *   keyboard  focusing already opened it, so Enter opens the record;
   *   touch     nothing opened it, so the first tap reveals — and the second,
   *             or the card's own Open action, navigates.
   *
   * It has to be measured at POINTER DOWN. On touch, the press fires `focus`
   * before `click`, our focus handler opens the card, and by the time `click`
   * arrives the card is open — so reading the state inside the click handler
   * gives exactly the wrong answer, and the first tap would navigate.
   */
  const revealOnly = useRef(false)
  const notePress = (key: string, pointerType: string) => {
    revealOnly.current = pointerType === 'touch' && openKey !== key
  }

  const focusPin = useCallback((key: string) => {
    setOpenKey(key)
    pinNodes.current.get(key)?.focus()
  }, [])

  /** Open on focus — unless this focus is the one Escape just handed back. */
  const revealOnFocus = useCallback((key: string) => {
    if (suppressReveal.current) return
    setOpenKey(key)
  }, [])

  /**
   * Escape: close AND return focus to the pin (the addendum's exact clause).
   *
   * ── AND THE REFOCUS MUST NOT RE-OPEN WHAT IT JUST CLOSED ───────────────
   * Focusing the pin fires its `onFocus`, which is one of the two ways a card
   * opens. So the naive version closed the card and immediately reopened it —
   * **Escape could never dismiss anything**, and the first version of this
   * component's own test could not see it, because it left focus on the pin the
   * whole time and asserted focus was on the pin afterwards. A mutation arm that
   * refused to go red is what exposed both.
   *
   * A ref rather than state, and read inside the event: the value is consulted
   * during a focus that this call itself causes, and rendering from it would
   * reintroduce exactly the staleness it exists to avoid.
   */
  const suppressReveal = useRef(false)
  const dismiss = useCallback((key: string) => {
    setOpenKey((current) => (current === key ? null : current))
    suppressReveal.current = true
    pinNodes.current.get(key)?.focus()
    // Cleared on the next task, not synchronously: the focus event this call
    // triggers is dispatched before control returns here in some browsers and
    // after it in others, and the flag has to survive both orders.
    queueMicrotask(() => { suppressReveal.current = false })
  }, [])

  /**
   * Pointer-leave: close, and move focus NOWHERE.
   *
   * Separate from `dismiss` deliberately. Folding the two together made a mouse
   * drifting off a pin pull focus back onto it — so a keyboard user tabbing past
   * the canvas was yanked back by a pointer they were not using.
   */
  const close = useCallback((key: string) => {
    setOpenKey((current) => (current === key ? null : current))
  }, [])

  const width = size.width || ASSUMED_WIDTH
  const tileRefs = useMemo(
    () => (tiles ? tilesFor(active, width, size.height, tiles.maxZoom ?? MAX_ZOOM) : []),
    [tiles, active, width, size.height])

  /* The heading elements, derived once. `h${n}` as a string is what React needs
     to render a dynamic intrinsic, and deriving both from ONE number is what
     stops the title and its lists drifting a level apart. */
  const Title = `h${headingLevel}` as 'h2' | 'h3' | 'h4'
  const SubTitle = `h${Math.min(headingLevel + 1, 6)}` as 'h3' | 'h4' | 'h5'

  const listId = `${domId}-results`
  const placedCount = pins.length
  const unplacedCount = unplaceable.length

  return (
    <section className={cx(s.root, presentation === 'record' && s.record)}
      data-testid={testId} data-presentation={presentation}>
      <header className={s.head}>
        {/* A real heading, so the two lists below have something to be under.
            As a `<span>` it left them parented by nothing and axe said so. */}
        <Title className={s.title}>{heading}</Title>
        {/* The count is a SENTENCE, not two numbers a reader has to subtract.
            "3 located" beside a list of 2 more is the fact the old panel's
            `N located` string carried and the only part of it worth keeping. */}
        <span className={s.count} data-testid={`${testId}-count`}>
          {placedCount === 0 && unplacedCount === 0 ? 'Nothing to show'
            : `${placedCount} located${unplacedCount > 0 ? ` · ${unplacedCount} without a location` : ''}`}
        </span>
        <span className={s.grow} />
        <div className={s.tools} role="group" aria-label="Map view">
          {/* Keyboard zoom is not a nicety: a wheel and a pinch are the only
              other ways in, and neither is available to somebody on a keyboard. */}
          <button type="button" className={s.tool} onClick={() => zoomBy(1)}
            disabled={active.zoom >= MAX_ZOOM} aria-label="Zoom in"
            data-testid={`${testId}-zoom-in`}>+</button>
          <button type="button" className={s.tool} onClick={() => zoomBy(-1)}
            disabled={active.zoom <= MIN_ZOOM} aria-label="Zoom out"
            data-testid={`${testId}-zoom-out`}>−</button>
          <button type="button" className={s.tool} onClick={reset}
            data-testid={`${testId}-reset`}>Reset view</button>
        </div>
      </header>

      {problem && <p className={s.problem} data-testid={`${testId}-problem`}>{problem}</p>}

      <div className={s.body}>
        <div
          ref={canvas}
          className={s.canvas}
          style={{ height: size.height }}
          data-testid={`${testId}-canvas`}
          data-tiles={tiles ? 'on' : 'off'}
          onPointerDown={onCanvasPointerDown}
          onPointerMove={onCanvasPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {tileRefs.map((tile) => (
            <img key={tile.key} className={s.tile} src={tileUrl(tiles!.urlTemplate, tile)}
              alt="" draggable={false} loading="lazy"
              style={{ left: tile.left, top: tile.top }} />
          ))}

          {!tiles && (
            /* OFFLINE / REFUSED, said out loud. The pins are still drawn over
               the plain ground and the list below is unaffected: a missing tile
               provider is a missing BACKDROP, not missing records. */
            <p className={s.ground} data-testid={`${testId}-no-tiles`}>
              No map provider is configured, so there is no backdrop. Locations are still listed.
            </p>
          )}

          {clusters.map((cluster) => (
            <MapMark
              key={cluster.key}
              cluster={cluster}
              view={active}
              width={width}
              height={size.height}
              open={cluster.members.length === 1 && openKey === cluster.members[0]!.key}
              onOpenRecord={onOpen}
              onReveal={setOpenKey}
              onRevealOnFocus={revealOnFocus}
              onDismiss={dismiss}
              onClose={close}
              notePress={notePress}
              revealOnly={revealOnly}
              onZoomTo={(members) => {
                setOpenKey(null)
                setView(fitView(members, width, size.height))
              }}
              registerNode={(key, node) => {
                if (node) pinNodes.current.set(key, node)
                else pinNodes.current.delete(key)
              }}
              testId={testId}
            />
          ))}

          {tiles && (
            <span className={s.attribution} data-testid={`${testId}-attribution`}>
              {tiles.attribution}
            </span>
          )}
        </div>

        {presentation === 'workspace' && (
          <div className={s.side}>
            {busy && pins.length === 0 && (
              <p className={s.muted} data-testid={`${testId}-busy`}>Looking…</p>
            )}
            {!busy && pins.length === 0 && unplaceable.length === 0 && (
              /* An honest empty state, not a blank canvas. */
              <p className={s.muted} data-testid={`${testId}-empty`}>{empty}</p>
            )}

            {pins.length > 0 && (
              <>
                {/* h3, not h4, and it is a real defect rather than a style
                    choice: the map's side lists are PEERS of the relationship
                    sections beside them, which are h3 under the workspace's own
                    h2. An h4 there skips a level, and `ci / a11y` catches it —
                    this file's own axe case did, with `heading-order`. The UI
                    skill records the same trap one component over, on
                    `RecordShell`'s `presentation` prop. */}
                <SubTitle className={s.sideHead} id={listId}>Locations</SubTitle>
                <ul className={s.list} aria-labelledby={listId} data-testid={`${testId}-list`}>
                  {pins.map((pin) => (
                    <li key={pin.key}>
                      <button type="button"
                        className={cx(s.listRow, openKey === pin.key && s.listRowOn)}
                        /* The list and the canvas are ONE selection. Activating a
                           row moves focus to its pin, which opens the pin's card
                           through the same path a hover does — so the two never
                           show different things. */
                        onClick={() => focusPin(pin.key)}
                        aria-current={openKey === pin.key ? 'true' : undefined}
                        data-testid={`${testId}-list-row`}
                        data-key={pin.key}>
                        <span className={s.listName}>{pin.title}</span>
                        {pin.typeLabel && <span className={s.listType}>{pin.typeLabel}</span>}
                        {pin.groupLabel && <span className={s.listGroup}>{pin.groupLabel}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {unplaceable.length > 0 && (
              <>
                {/* NAMED, and named the same way every time — this heading is the
                    contract that an unplottable record is still reachable. */}
                <SubTitle className={s.sideHead} data-testid={`${testId}-unavailable-head`}>
                  Location unavailable
                </SubTitle>
                <ul className={s.list} data-testid={`${testId}-unavailable`}>
                  {unplaceable.map((record) => {
                    const openable = !!onOpen && record.openable !== false
                    const body = (
                      <>
                        <span className={s.listName}>{record.title}</span>
                        {record.typeLabel && <span className={s.listType}>{record.typeLabel}</span>}
                        <span className={s.listWhy}>{record.reason ?? 'No coordinates yet'}</span>
                      </>
                    )
                    return (
                      <li key={record.key}>
                        {openable
                          ? <button type="button" className={s.listRow}
                              onClick={() => onOpen!(record)}
                              data-testid={`${testId}-unavailable-row`}>{body}</button>
                          /* Not a disabled button: a disabled control is skipped
                             by keyboard navigation entirely, so the record would
                             be announced as a control AND be unreachable. */
                          : <div className={s.listRow} data-openable="false"
                              data-testid={`${testId}-unavailable-row`}>{body}</div>}
                      </li>
                    )
                  })}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      {presentation === 'record' && unplaceable.length > 0 && (
        <p className={s.recordWhy} data-testid={`${testId}-record-unavailable`}>
          {unplaceable[0]!.reason ?? 'This record has no coordinates yet, so it cannot be placed on the map.'}
        </p>
      )}
    </section>
  )
}

/** Move a view by a fraction of the world, in mercator units. */
function panBy(view: MapView, dx: number, dy: number): MapView {
  const x = mercX(view.lng) - dx
  const y = mercY(view.lat) - dy
  const lng = wrapLng(x * 360 - 180)
  const n = Math.PI - 2 * Math.PI * y
  const lat = clampLat((180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n))))
  return { lat, lng, zoom: view.zoom }
}

/**
 * One mark on the canvas: a single record's pin, or a cluster's count.
 *
 * A cluster is NOT a pin with a badge. It has no record behind it, so it has no
 * preview card and no Open — activating it zooms to its members, which is the
 * only honest action a mark standing for several records can offer.
 */
function MapMark({
  cluster, view, width, height, open, onOpenRecord, onReveal, onRevealOnFocus, onDismiss, onClose, notePress,
  revealOnly, onZoomTo, registerNode, testId,
}: {
  cluster: Cluster<MapPin>
  view: MapView
  width: number
  height: number
  open: boolean
  onOpenRecord?: (pin: MapPin) => void
  onReveal: (key: string) => void
  /** Focus-driven open, which Escape's own refocus must not trigger. */
  onRevealOnFocus: (key: string) => void
  onDismiss: (key: string) => void
  onClose: (key: string) => void
  notePress: (key: string, pointerType: string) => void
  revealOnly: React.RefObject<boolean>
  onZoomTo: (members: MapPin[]) => void
  registerNode: (key: string, node: HTMLButtonElement | null) => void
  testId: string
}) {
  const point = project(cluster, view, width, height)
  const style = { left: point.x, top: point.y }
  // Off-canvas marks are not rendered. They are still in the LIST, which is the
  // structure that guarantees reachability — drawing them would put buttons
  // outside the visible box that a keyboard would still walk into.
  const outside = point.x < -40 || point.y < -40 || point.x > width + 40 || point.y > height + 40
  if (outside) return null

  if (cluster.members.length > 1) {
    const names = cluster.members.slice(0, 3).map((m) => m.title).join(', ')
    const more = cluster.members.length - 3
    return (
      <button type="button" className={s.cluster} style={style}
        data-testid={`${testId}-cluster`} data-count={cluster.members.length}
        aria-label={`${cluster.members.length} locations here: ${names}${more > 0 ? ` and ${more} more` : ''}. Zoom in to separate them.`}
        onClick={() => onZoomTo(cluster.members)}>
        <span className={cx(s.clusterN, 'tnum')}>{cluster.members.length}</span>
      </button>
    )
  }

  const pin = cluster.members[0]!
  const openable = !!onOpenRecord && pin.openable !== false
  const cardId = `${testId}-card-${pin.key}`
  // The accessible name carries the address AND the type, so the card's content
  // is available without ever producing a hover (owner addendum).
  const label = [pin.title, pin.typeLabel, pin.subtitle, pin.groupLabel]
    .filter(Boolean).join(', ')

  return (
    <div className={s.mark} style={style} data-mark={pin.key}
      /* The card is a SIBLING inside this wrapper, so Tab reaches its Open
         action naturally and there is no focus trap to build or escape. */
      onPointerLeave={() => { if (open) closeUnlessFocused(pin.key, onClose) }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          // Dismiss and RETURN FOCUS to the pin — the addendum's exact clause.
          e.stopPropagation()
          onDismiss(pin.key)
        }
      }}>
      <button
        type="button"
        ref={(node) => registerNode(pin.key, node)}
        className={cx(s.pin, open && s.pinOn, !openable && s.pinFlat)}
        data-testid={`${testId}-pin`}
        data-key={pin.key}
        data-openable={openable ? undefined : 'false'}
        aria-label={label}
        aria-expanded={open}
        aria-controls={open ? cardId : undefined}
        onPointerDown={(e) => notePress(pin.key, e.pointerType)}
        onPointerEnter={() => onReveal(pin.key)}
        onFocus={() => onRevealOnFocus(pin.key)}
        onClick={() => {
          if (revealOnly.current) { revealOnly.current = false; return }
          if (openable) onOpenRecord!(pin)
        }}
      >
        <span className={s.dot} aria-hidden="true" />
      </button>

      {open && (
        <div className={s.card} id={cardId} role="group"
          aria-label={`Details for ${pin.title}`} data-testid={`${testId}-preview`}>
          <p className={s.cardTitle}>{pin.title}</p>
          {pin.subtitle && <p className={s.cardSub}>{pin.subtitle}</p>}
          {/* The governed type label, rendered verbatim. If the host had no
              metadata for it the line is ABSENT rather than showing a code —
              an honest gap beats `billing` leaking onto a customer's screen. */}
          {pin.typeLabel && <p className={s.cardType} data-testid={`${testId}-preview-type`}>{pin.typeLabel}</p>}
          {pin.groupLabel && <p className={s.cardGroup}>{pin.groupLabel}</p>}
          {openable && (
            <button type="button" className={s.cardOpen}
              data-testid={`${testId}-preview-open`}
              onClick={() => onOpenRecord!(pin)}>Open</button>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Close on pointer-leave, but NOT while the pin (or something inside its card)
 * has focus.
 *
 * Moving the mouse away from a pin somebody is tabbed onto must not silently
 * remove the thing their focus is announcing — and moving the pointer INTO the
 * card must not close it either, which is the addendum's clause and which falls
 * out of the card being inside the wrapper this handler is on.
 *
 * The two open paths are independent, so the closing ones have to be too.
 */
function closeUnlessFocused(key: string, close: (key: string) => void) {
  const active = typeof document === 'undefined' ? null : document.activeElement
  if (active instanceof HTMLElement && active.closest(`[data-mark="${key}"]`)) return
  close(key)
}
